import { pdf } from '@react-pdf/renderer';

import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  fetchCompleteTkv2OrderAPI,
  fetchPostReportAPI,
  fetchPostReportSignatureAPI,
  fetchPutTkv2CommentaryAttachmentAPI,
} from '../../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../../hooks/api/uploadBlob.ts';
import { setAccountSignatureBlobId } from '../../../redux/features/accountSlice.ts';
import { selectCommentary } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { CertifyModalFormOutput } from '../schemas.ts';
import { Tkv2PDF } from '../../../modules/tkv2PDF/types.ts';
import { Tkv2PDFModule } from '../../../modules/tkv2PDF/Tkv2PDFModule.tsx';
import { generateTkv2PDFChartImages } from '../../../modules/tkv2PDF/utils/generateTkv2PDFChartImages.ts';
import { AloftTime } from '@aloft/shared-objects';

export const useCertifyModalSubmission = ({
  orderID,
  tkv2PDFData,
}: {
  orderID: string;
  tkv2PDFData: Tkv2PDF;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [submissionText, setSubmissionText] = useState('');

  const context = useToolkitContext();
  const { fetcherOptions } = context;
  const dispatch = useAppDispatch();
  const commentary = useAppSelector((state) => selectCommentary(state));

  const submitModal = async (form: UseFormReturn<CertifyModalFormOutput>) => {
    setSubmissionText('Signing the report...');
    setIsSubmitting(true);

    try {
      const formValues = form.getValues();
      const { signatureFile } = formValues;
      const { stateSealFile } = formValues;

      if (!signatureFile) {
        setIsSubmitting(false);
        return;
      }

      const signatureBlob = await uploadBlob(signatureFile, 'report-signature', context);
      let stateSealBlob;

      if (stateSealFile) {
        stateSealBlob = await uploadBlob(stateSealFile, 'report-state-seal', context);
      }

      await fetchPostReportSignatureAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          signatureBlobId: signatureBlob.id,
          stateSealBlobId: stateSealBlob?.id,
          storeSignatureBlobId: formValues.saveSignature,
        },
      });

      if (formValues.saveSignature) {
        dispatch(setAccountSignatureBlobId(signatureBlob.id));
      }

      await fetchPutTkv2CommentaryAttachmentAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          ...commentary,
          reconciliation: {
            ...commentary.reconciliation,
            certificationCommentary: formValues.certificationCommentary,
          },
        },
      });

      setSubmissionText('Creating a copy of the report...');

      const chartImages = await generateTkv2PDFChartImages(tkv2PDFData.chartParams);
      const signatureImages = {
        signatureImage: URL.createObjectURL(signatureFile),
        stateSealImage: stateSealFile ? URL.createObjectURL(stateSealFile) : undefined,
      };
      const signedAt = AloftTime.now().toAloftDate().format('MM/dd/yyyy');


      const reportBlob = await pdf(
        <Tkv2PDFModule
          {...tkv2PDFData}
          tkv2ChartImages={chartImages}
          signedAt={signedAt}
          signatureImages={signatureImages}
        />,
      ).toBlob();

      const uploadedReportBlob = await uploadBlob(reportBlob, 'report-pdf', context);

      await fetchPostReportAPI({
        ...fetcherOptions,
        pathParams: { orderID },
        body: {
          reportBlobId: uploadedReportBlob.id,
        },
      });

      setSubmissionText('Delivering the report...');

      await fetchCompleteTkv2OrderAPI({
        ...fetcherOptions,
        pathParams: { orderID },
      });
      setIsCompleted(true);
      setIsSubmitting(false);
    } catch (error) {
      setIsError(true);
      setIsSubmitting(false);
    }
  };

  return {
    submitModal,
    isSubmitting,
    isError,
    isCompleted,
    submissionText,
  };
};
